import React, { useState } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = [{
  title: '1. Información que Recopilamos',
  text: 'Recopilamos únicamente la información necesaria para procesar tu compra: nombre, dirección de correo electrónico y datos de pago. No almacenamos información de tarjetas de crédito en nuestros servidores.'
}, {
  title: '2. Uso de la Información',
  text: 'Tu información personal se utiliza exclusivamente para procesar tu pedido, enviarte el producto digital adquirido y comunicarnos contigo en caso de ser necesario respecto a tu compra.'
}, {
  title: '3. Protección de Datos',
  text: 'Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración, divulgación o destrucción.'
}, {
  title: '4. Compartir Información',
  text: 'No vendemos, intercambiamos ni transferimos tu información personal a terceros. Esto no incluye terceros de confianza que nos asisten en la operación de nuestro sitio web o en la conducción de nuestro negocio, siempre que dichas partes acuerden mantener esta información confidencial.'
}, {
  title: '5. Cookies',
  text: 'Utilizamos cookies para mejorar tu experiencia de navegación y analizar el tráfico del sitio. Puedes configurar tu navegador para rechazar cookies, aunque esto puede afectar la funcionalidad del sitio.'
}, {
  title: '6. Tus Derechos',
  text: 'Tienes derecho a acceder, rectificar o eliminar tus datos personales en cualquier momento. Para ejercer estos derechos, puedes contactarnos a través de los canales indicados.'
}, {
  title: '7. Cambios en esta Política',
  text: 'Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Los cambios serán publicados en esta página con la fecha de última actualización.'
}];
const termsContent = [{
  title: '1. Aceptación de los Términos',
  text: 'Al acceder y realizar una compra en este sitio, aceptas cumplir y estar sujeta a estos términos y condiciones de servicio.'
}, {
  title: '2. Producto Digital',
  text: '"El Espejo Roto" es un producto digital en formato PDF. Al completar la compra, recibirás acceso inmediato para descargar el material. Debido a la naturaleza digital del producto, no se aceptan devoluciones una vez descargado.'
}, {
  title: '3. Uso Personal',
  text: 'El contenido adquirido es para uso personal y exclusivo. Queda prohibida la reproducción, distribución, modificación o reventa total o parcial del material sin autorización expresa por escrito.'
}, {
  title: '4. Propiedad Intelectual',
  text: 'Todo el contenido, incluyendo textos, diseños, gráficos y metodologías, está protegido por derechos de autor y es propiedad exclusiva de El Espejo Roto.'
}, {
  title: '5. Descargo de Responsabilidad',
  text: 'Este producto es de carácter informativo y educativo. No sustituye el tratamiento médico, psicológico o psiquiátrico profesional. Si te encuentras en una situación de peligro inmediato, contacta a las autoridades locales de tu país.'
}, {
  title: '6. Limitación de Responsabilidad',
  text: 'No nos hacemos responsables de los resultados individuales obtenidos con el uso del material. Los resultados pueden variar según las circunstancias personales de cada usuaria.'
}, {
  title: '7. Pagos y Seguridad',
  text: 'Todos los pagos se procesan a través de plataformas seguras con cifrado de alta seguridad. Los precios están expresados en dólares estadounidenses (USD) y pueden estar sujetos a impuestos según tu jurisdicción.'
}, {
  title: '8. Modificaciones',
  text: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigor inmediatamente después de su publicación en el sitio.'
}];
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  const isPrivacy = type === 'privacy';
  const title = isPrivacy ? 'Política de Privacidad' : 'Términos y Condiciones';
  const content = isPrivacy ? privacyContent : termsContent;
  const lastUpdated = 'Abril 2026';
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose} data-id="element-41">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.3,
      ease: 'easeOut'
    }} className="bg-dark-800 border border-gold/20 rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-[0_0_40px_rgba(0,0,0,0.5)]" onClick={e => e.stopPropagation()} data-id="element-42">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-dark-700" data-id="element-43">
          <h3 className="text-xl md:text-2xl font-serif text-gold" data-id="element-44">{title}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-dark-700 transition-colors text-offwhite/50 hover:text-offwhite" aria-label="Cerrar" data-id="element-45">
            <X className="w-5 h-5" data-id="element-46" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)] custom-scrollbar" data-id="element-47">
          <p className="text-offwhite/40 text-sm mb-6" data-id="element-48">
            Última actualización: {lastUpdated}
          </p>

          <div className="space-y-6" data-id="element-49">
            {content.map((section, index) => <div key={index} data-id="element-50">
                <h4 className="text-offwhite font-semibold mb-2" data-id="element-51">
                  {section.title}
                </h4>
                <p className="text-offwhite/70 font-light leading-relaxed text-sm" data-id="element-52">
                  {section.text}
                </p>
              </div>)}
          </div>
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="py-8 px-4 bg-dark-900 border-t border-dark-800 text-center" data-id="element-53">
        <div className="max-w-4xl mx-auto" data-id="element-54">
          <div className="flex items-center justify-center gap-6 mb-4" data-id="element-55">
            <button onClick={() => setActiveModal('privacy')} className="text-offwhite/50 text-sm font-light hover:text-gold transition-colors" data-id="element-56">
              Política de Privacidad
            </button>
            <span className="text-offwhite/20" data-id="element-57">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-offwhite/50 text-sm font-light hover:text-gold transition-colors" data-id="element-58">
              Términos y Condiciones
            </button>
          </div>

          <p className="text-offwhite/40 text-sm font-light" data-id="element-59">
            © {new Date().getFullYear()} El Espejo Roto. Todos los derechos
            reservados.
          </p>
          <p className="text-offwhite/30 text-xs mt-2 font-light max-w-2xl mx-auto" data-id="element-60">
            Este producto no sustituye el tratamiento médico o psicológico
            profesional. Si te encuentras en peligro físico inmediato, por favor
            contacta a las autoridades locales de tu país.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-61">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-62" />}
      </AnimatePresence>
    </>;
}